import { logger } from '../utils/Logger';

// PROD-9012. The incoming ring is started from a websocket relay callback, so
// there is never a user activation in scope. Two constraints follow:
//
// 1. The element has to be the SAME one for every ring. WebKit unblocks an
//    individual HTMLMediaElement once that element has played inside an
//    activation, so a per-ring `new Audio()` starts out blocked every time.
// 2. It has to be primed by a gesture that arrives BEFORE the call, so the
//    listeners below are installed at import and kept for the life of the
//    page (an element can be re-blocked, e.g. after an OS interruption).
//
// Safari also refuses to start a detached element, so the ring lives in the
// document — same reason twilio_audio.js adopts the SDK's remote <audio>.

const RING_SRC = '/static/sounds/phone-ring.mp3';

let ringAudio: HTMLAudioElement | null = null;
let primed = false;
let wantPlaying = false;
let gestureUnlockInstalled = false;

const getRingAudio = (): HTMLAudioElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  if (!ringAudio) {
    const el = document.createElement('audio');
    el.src = RING_SRC;
    el.loop = true;
    el.volume = 1;
    el.preload = 'auto';
    el.setAttribute('playsinline', '');
    el.setAttribute('aria-hidden', 'true');
    el.style.display = 'none';
    try {
      document.body.appendChild(el);
    } catch (error) {
      logger.debug('ring_audio: failed to attach ring element.', error);
    }
    ringAudio = el;
  }

  return ringAudio;
};

const attemptPlay = (el: HTMLAudioElement) => {
  try {
    const played = el.play();
    if (played && typeof played.catch === 'function') {
      played.catch((error) => {
        // Not fatal: onUserGesture retries on the agent's next click.
        logger.debug('ring_audio: ring playback blocked.', error);
      });
    }
  } catch (error) {
    logger.debug('ring_audio: ring playback threw.', error);
  }
};

// A muted play()/pause() inside the gesture is what unblocks the element for
// the relay callback later; the agent hears nothing.
const primeRingAudio = (el: HTMLAudioElement) => {
  if (primed || wantPlaying) {
    return;
  }

  el.muted = true;

  const settle = (unblocked: boolean) => {
    el.muted = false;
    primed = primed || unblocked;
    // A ring that started while this prime was in flight owns the element now.
    if (wantPlaying) {
      return;
    }
    try {
      el.pause();
      el.currentTime = 0;
    } catch (error) {
      logger.debug('ring_audio: failed to reset primed ring element.', error);
    }
  };

  let played;
  try {
    played = el.play();
  } catch (error) {
    logger.debug('ring_audio: ring prime threw.', error);
    settle(false);
    return;
  }

  if (played && typeof played.then === 'function') {
    played.then(() => settle(true)).catch(() => settle(false));
  } else {
    settle(true);
  }
};

const onUserGesture = () => {
  const el = getRingAudio();
  if (!el) {
    return;
  }

  if (wantPlaying) {
    if (el.paused) {
      attemptPlay(el);
    }
    return;
  }

  primeRingAudio(el);
};

export const installRingUnlock = (): void => {
  if (gestureUnlockInstalled || typeof document === 'undefined') {
    return;
  }
  gestureUnlockInstalled = true;
  const opts = { capture: true, passive: true };
  document.addEventListener('pointerdown', onUserGesture, opts);
  document.addEventListener('keydown', onUserGesture, opts);
};

export const startRing = (): void => {
  installRingUnlock();
  const el = getRingAudio();
  if (!el) {
    return;
  }

  wantPlaying = true;
  el.muted = false;
  try {
    el.currentTime = 0;
  } catch (error) {
    logger.debug('ring_audio: failed to rewind ring element.', error);
  }
  attemptPlay(el);
};

export const stopRing = (): void => {
  wantPlaying = false;
  if (!ringAudio) {
    return;
  }

  try {
    ringAudio.pause();
    ringAudio.currentTime = 0;
  } catch (error) {
    logger.debug('ring_audio: failed to stop ring audio.', error);
  }
};

installRingUnlock();
